/**
 * Pure helpers for previewing a fragment draft in the fragment editor.
 *
 * The draft's (possibly unsaved) source is registered in the preview
 * fragment map under its own key — or DRAFT_FRAGMENT_KEY while creating,
 * before a key is typed — so the preview always renders the edit buffer,
 * and nested includes/extends resolve against it too.
 */

/** Registry key for a draft that doesn't have a (valid) key yet. */
export const DRAFT_FRAGMENT_KEY = '__draft-fragment__';

/** The last path segment of a fragment key ("partials/xxxl/footer" → "footer"). */
export function fragmentLeaf(key: string): string {
  const segments = key.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? key;
}

/**
 * All `{% block name %}` names declared in a layout source, in order.
 *
 * Also read by the visual editor's preflight, which has to know whether the
 * block its compiler targets actually exists in the layout — content emitted
 * into a block a layout never declares is discarded by Twig, with no error.
 */
export function layoutBlocks(layoutSource: string): string[] {
  return [...layoutSource.matchAll(/\{%-?\s*block\s+(\w+)/g)].map(
    (match) => match[1],
  );
}

/**
 * The block of a layout a partial should preview inside, given the partial's
 * name. Layouts name their content blocks after the partials that fill them
 * (xxxl/base has `appointment_details`, `footer`, … blocks), so the natural
 * slot is the block matching the partial's leaf name. Falls back to `body`
 * when present. Returns null when the layout offers no sensible slot — the
 * caller then previews the partial on its own rather than forcing it into an
 * unrelated block (which renders hidden or empty).
 */
export function chooseLayoutBlock(
  layoutSource: string,
  preferredName: string | null,
): string | null {
  const blocks = layoutBlocks(layoutSource);
  if (preferredName && blocks.includes(preferredName)) return preferredName;
  if (blocks.includes('body')) return 'body';
  return null;
}

/**
 * True when the content is table-internal markup (a bare `<tr>`/`<td>`/…),
 * which a browser drops unless it sits inside a `<table>`. Such partials need
 * a table scaffold to be visible when previewed on their own.
 */
export function isTableInternalContent(content: string): boolean {
  return /^\s*<(tr|td|th|thead|tbody|tfoot)\b/i.test(content);
}

/**
 * Builds the Twig source the fragment-editor preview renders.
 *
 * - A layout draft previews as itself: a stub child extends it, so the
 *   layout's own default block content shows.
 * - A partial draft previews inside the selected layout's matching block
 *   (by name, else `body`) for visual context (fonts, widths, chrome).
 * - When no layout is selected, or the layout has no matching/`body` block,
 *   the partial is previewed on its own — it is, after all, only a part of an
 *   email — wrapped in a table scaffold when it is table-internal markup so
 *   it stays visible.
 */
export function buildFragmentPreviewSource(options: {
  draftKey: string;
  fragmentType: string;
  previewLayoutKey?: string | null;
  previewLayoutSource?: string | null;
  /** The draft's own content, used to decide the standalone scaffold. */
  partialContent?: string;
}): string {
  const {
    draftKey,
    fragmentType,
    previewLayoutKey,
    previewLayoutSource,
    partialContent = '',
  } = options;

  if (fragmentType === 'layout') {
    /**
     * Fill every region the layout declares, rather than extending it bare.
     *
     * `{% extends %}` on its own leaves each `{% block %}` empty, so the preview
     * was the layout wrapped around nothing — technically correct and useless: you
     * could not see where the email would go, and a layout whose chrome is mostly
     * whitespace looked broken.
     *
     * Every declared region gets its own marker, named after itself, because a
     * layout with several (`layout/xxxl/base` has nine) is exactly the case where
     * "which block is which" is the question being asked.
     *
     * `subject` and `preheader` are skipped: they are not content regions —
     * one is the <title>, the other the hidden inbox preview line — and marking
     * them would put placeholder text in the rendered chrome.
     */
    /** True when the layout leaves this block empty, i.e. it is a content slot. */
    const isEmptyBlock = (name: string): boolean => {
      const open = new RegExp(`\\{%-?\\s*block\\s+${name}\\b[^%]*%\\}`);
      const at = partialContent.search(open);
      if (at < 0) return false;
      const rest = partialContent.slice(at).replace(open, '');
      const end = rest.search(/\{%-?\s*endblock/);
      return end >= 0 && rest.slice(0, end).trim() === '';
    };

    /**
     * Only the blocks that are **empty** in the layout.
     *
     * A `{% block %}` with a body is the layout's own chrome — `partner_base`
     * keeps its social row and legal line inside `{% block footer %}` so a
     * template can replace them. Overriding it to mark the region therefore
     * *deleted the very thing being previewed*: the footer rendered as a
     * placeholder box and the socials never appeared, which read as a twig.js
     * bug and is not one. Measured: the registered source grew by 2 kB of new
     * footer markup while the rendered output stayed at exactly 4498 bytes.
     *
     * An empty block is a genuine slot — that is where content lands and where a
     * marker belongs. A filled one renders itself.
     */
    const regions = layoutBlocks(partialContent).filter(
      (name) =>
        name !== 'subject' &&
        name !== 'preheader' &&
        // `content`/`body` are the conventional slot names and are marked even
        // if the layout puts a default inside them — that default is a stand-in
        // for the email, not chrome, so showing where the email goes wins.
        (isEmptyBlock(name) || name === 'content' || name === 'body'),
    );

    /**
     * **The marker has to match the block's position in the table.**
     *
     * Email layouts are tables, and a `{% block %}` can sit at two levels: inside
     * a `<td>` (like `content`), or wrapping a whole `<tr>` (like `footer`).
     * Overriding a row-level block with a bare `<div>` produces a div directly
     * inside `<tbody>` — invalid markup that the browser *hoists out above the
     * table*. That is what made the footer marker appear above the content and
     * the layout look inside-out; verified from the rendered `srcdoc`, where the
     * document order was right and only the parse was wrong.
     *
     * So the marker is wrapped in its own `<tr><td>` when the block it replaces
     * begins a row, and left bare otherwise.
     */
    const box =
      `<div style="margin:8px 0;padding:14px 12px;` +
      `border:2px dashed #B7B7BC;border-radius:6px;background:#FAFAFB;` +
      `font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:700;` +
      `text-transform:uppercase;letter-spacing:0.06em;color:#6B6B72;` +
      `text-align:center;">`;

    const startsARow = (name: string): boolean => {
      const at = partialContent.search(
        new RegExp(`\\{%-?\\s*block\\s+${name}\\b`),
      );
      if (at < 0) return false;
      // Non-greedy to the *first* `%}`: `[^%]*%\}` needed the first `%` to be
      // the closing one, so it never matched `{% block footer %}` at all.
      const after = partialContent.slice(at).replace(/^[\s\S]*?%\}/, '');
      return /^\s*<tr\b/i.test(after);
    };

    const marker = (name: string) => {
      const inner = `${box}${name}</div>`;
      const body = startsARow(name)
        ? `<tr><td colspan="99" style="padding:0;">${inner}</td></tr>`
        : inner;
      return `{% block ${name} %}${body}{% endblock %}`;
    };

    return [`{% extends '${draftKey}' %}`, ...regions.map(marker)].join('\n');
  }

  const include = `{% include '${draftKey}' %}`;

  const block =
    previewLayoutKey && previewLayoutSource
      ? chooseLayoutBlock(previewLayoutSource, fragmentLeaf(draftKey))
      : null;
  if (previewLayoutKey && block) {
    return `{% extends '${previewLayoutKey}' %}\n{% block ${block} %}${include}{% endblock %}`;
  }

  // Standalone preview — always shows something. Table-internal partials need
  // a table ancestor or the browser discards them.
  return isTableInternalContent(partialContent)
    ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tbody>${include}</tbody></table>`
    : include;
}

/**
 * The fragment registry the editor's preview renders against: the catalog with
 * the draft's own key pointing at the edit buffer, so the preview — and any
 * nested include/extends — resolves to what is being typed.
 *
 * The two fallbacks are what make it correct rather than obvious:
 *
 * - **An empty buffer does not override a stored source.** `draftContent` is a
 *   watched form value and is empty for the first render after the drawer
 *   opens. Overriding with it registered the fragment as an *empty template*,
 *   and for a layout that is fatal — `{% extends %}` resolves to nothing and
 *   the preview renders either blank or as the child's blocks with none of the
 *   layout around them.
 * - **…but a fragment being created is registered empty anyway.** It has no
 *   stored source to fall back to, so an unregistered key left twig.js
 *   reporting *"Unable to find template file"* — a rendering-failed warning on
 *   every new block before a character was typed. An empty preview is the
 *   truthful rendering of an empty block.
 */
export function buildPreviewFragments(options: {
  fragmentSources: Record<string, string>;
  draftKey: string;
  draftContent: string;
}): Record<string, string> {
  const { fragmentSources, draftKey, draftContent } = options;
  if (draftContent) return { ...fragmentSources, [draftKey]: draftContent };
  return draftKey in fragmentSources
    ? fragmentSources
    : { ...fragmentSources, [draftKey]: '' };
}
